import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/react-hooks";
import { Box } from "@mui/material";
import {
  OcCardButtonBar,
  OcConfirmationDialog,
  OcEmailInputField,
} from "oc/components";
import { OC_UPDATE_PROPERTY } from "oc/graphql";
import { useOcEmailInput, useOcProperty, useOcSnackbar } from "oc/hooks";

type MyLeaveEmailFormProps = {
  setLoading: (loading: boolean) => void;
  type: string;
  emailByCompanyState: boolean;
  emailByCompany: boolean;
  isMobile: boolean;
};

export const MyLeaveEmailForm: React.FC<MyLeaveEmailFormProps> = ({
  setLoading,
  type,
  emailByCompanyState,
  emailByCompany,
  isMobile,
}) => {
  const { t } = useTranslation();
  const { setOpenMessage } = useOcSnackbar();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const {
    value: email,
    setValue: setEmail,
    isValid: isEmailValid,
    validate: validateEmail,
    error: emailError,
    setError: setEmailError,
    helperText: emailHelperText,
    setHelperText: setEmailHelperText,
  } = useOcEmailInput();

  const { data: emailProperty } = useOcProperty({
    resource: "myLeave",
    key: type,
    onCompleted: ({ value }) => {
      if (!email && value) {
        setEmail(value);
      }
    },
  });

  const [updateProperty] = useMutation(OC_UPDATE_PROPERTY, {
    onCompleted: () => {
      setLoading(false);
      setOpenMessage({
        type: "success",
        message: t("OC_PROPERTY_SAVE_SUCCEEDED"),
      });
    },
    onError: () => {
      setLoading(false);
      setOpenMessage({
        type: "error",
        message: t("OC_PROPERTY_SAVE_FAILED"),
      });
    },
  });

  let disabled = false;
  if (emailProperty === email && emailByCompany === emailByCompanyState) {
    disabled = true;
  }

  useEffect(() => {
    if (emailProperty && !email) {
      setEmail(emailProperty.value);
    }
  }, [emailProperty, email, setEmail]);

  const handleCancel = () => {
    setEmail(emailProperty?.value || "");
    setEmailError(false);
    setEmailHelperText("");
  };

  const handleSave = () => {
    if (!isEmailValid()) {
      validateEmail();
      setShowConfirmDialog(false);
      return;
    }
    setLoading(true);
    updateProperty({
      variables: {
        resource: "myLeave",
        key: type,
        value: email,
      },
    });
    if (!emailByCompanyState) {
      updateProperty({
        variables: {
          resource: "myLeave",
          key:
            type === "administrativeEmail"
              ? "administrativeEmailByCompany"
              : "payrollEmailByCompany",
          value: emailByCompanyState ? "true" : "false",
        },
      });
    }
    setShowConfirmDialog(false);
  };

  const cardLabel =
    type === "administrativeEmail"
      ? t("MY_LEAVE_ADMINISTRATIVE_EMAIL")
      : t("MY_LEAVE_PAYROLL_EMAIL_ADDRESS");

  const handleDialogCancel = () => {
    setShowConfirmDialog(false);
  };

  return (
    <>
      <OcConfirmationDialog
        showDialog={showConfirmDialog}
        onClose={handleSave}
        onCancel={handleDialogCancel}
        title={t("OC_CONFIRM_SETTING")}
        texts={[t("OC_CONFIRM_SETTING_INFO")]}
        okButtonText={t("OC_ALL_RIGHT")}
        cancelButtonText={t("OC_CANCEL")}
      />
      <Box>
        <Box
          width={isMobile ? "auto" : "432px"}
          paddingLeft={isMobile ? "0px" : "16px"}
        >
          <OcEmailInputField
            value={email || ""}
            setValue={setEmail}
            error={emailError}
            setError={setEmailError}
            helperText={emailHelperText}
            setHelperText={setEmailHelperText}
            label={cardLabel}
          />
        </Box>
        <OcCardButtonBar
          handleRightButton={handleCancel}
          rightButtonText={t("OC_CANCEL")}
          rightButtonColor="secondary"
          handlePrimaryButton={() => setShowConfirmDialog(true)}
          primaryButtonText={t("OC_SAVE")}
          primaryButtonDisabled={disabled}
        />
      </Box>
    </>
  );
};

export default MyLeaveEmailForm;
